import React, { Component } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationTriangle,
  faCopy,
  faCheck,
  faHome,
  faRedo,
} from '@fortawesome/free-solid-svg-icons';
import { ErrorBoundaryProps, ErrorBoundaryState } from './types';
import { styles } from './styles';
import { Button } from '../Button';

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, copied: false };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.setState({ errorInfo });
  }

  private buildErrorReport(): string {
    const { error, errorInfo } = this.state;
    const lines: string[] = [
      `Error: ${error?.name ?? 'Unknown'}`,
      `Message: ${error?.message ?? 'No message'}`,
      '',
      'Stack Trace:',
      error?.stack ?? 'No stack trace available',
    ];

    if (errorInfo?.componentStack) {
      lines.push('', 'Component Stack:', errorInfo.componentStack);
    }

    lines.push('', `URL: ${window.location.href}`);
    lines.push(`Timestamp: ${new Date().toISOString()}`);
    lines.push(`User Agent: ${navigator.userAgent}`);

    return lines.join('\n');
  }

  private handleCopy = async (): Promise<void> => {
    const report = this.buildErrorReport();
    await navigator.clipboard.writeText(report);
    this.setState({ copied: true });
    setTimeout(() => this.setState({ copied: false }), 2000);
  };

  private handleGoHome = (): void => {
    window.location.href = '/';
  };

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): React.ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { error, errorInfo, copied } = this.state;

    return (
      <div className={styles.container}>
        <div className={styles.icon}>
          <FontAwesomeIcon icon={faExclamationTriangle} />
        </div>
        <h2 className={styles.title}>Something went wrong</h2>
        <p className={styles.message}>
          An unexpected error occurred. You can try reloading the page or going back to the home
          page. If the problem persists, copy the error details below and share them with the
          support team.
        </p>

        <div className={styles.actions}>
          <Button
            variant="ghost-primary"
            onClick={this.handleGoHome}
            icon={<FontAwesomeIcon icon={faHome} />}>
            Go to Home
          </Button>
          <Button
            variant="ghost-primary"
            onClick={this.handleReload}
            icon={<FontAwesomeIcon icon={faRedo} />}>
            Reload Page
          </Button>
        </div>

        <div className={styles.detailsContainer}>
          <div className={styles.detailsHeader}>
            <span className={styles.detailsLabel}>Error Details</span>
            <Button
              variant={copied ? 'ghost-success' : 'ghost-primary'}
              size="sm"
              onClick={this.handleCopy}
              icon={<FontAwesomeIcon icon={copied ? faCheck : faCopy} />}>
              {copied ? 'Copied!' : 'Copy to clipboard'}
            </Button>
          </div>

          <div className={styles.errorBlock}>
            <div className={styles.errorName}>{error?.name}</div>
            <div className={styles.errorMessage}>{error?.message}</div>
            <div className={styles.stackTrace}>{error?.stack}</div>

            {errorInfo?.componentStack && (
              <div className={styles.componentStack}>
                <div className={styles.componentStackLabel}>Component Stack</div>
                <div className={styles.stackTrace}>{errorInfo.componentStack}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
export type { ErrorBoundaryProps } from './types';
